import { updateLoggedInEmployeeAction } from 'actions/loggedInEmployee';
import {
  authenticateApi,
  authenticateDemoUserApi,
  authenticateNetchexApi,
  employeeDetailsApi,
  employeeReliabilityScoreApi,
  getEmployeeTeamsApi,
  getLeaderboardApi,
  getLeaderboardTrophiesApi,
  getMeshTrophiesApi,
  getNodesApi,
  getRelationshipsApi,
  loginApi,
  nominateForTrophyApi,
  removeEmployeeApi,
  setUserTimeZoneApi,
  uploadProfilePhotoApi,
} from 'api/employee';
import Employee from 'models/employee';
import HashTag from 'models/hashtag';
import OrgTeam from 'models/orgTeam';
import PendingInvite from 'models/pendingInvite';
import Team from 'models/team';

import {
  getEmployeeObjectFromRes,
  parseLeaderboardRes,
  parseNodesRes,
  parseRelationshipsRes,
} from './helpers/employee';

// returns bool response with respect to success or failure of operation
export const uploadProfilePhoto = ({ fileObject, employeeId }) =>
  uploadProfilePhotoApi(fileObject, employeeId).then((res) => {
    const employee = new Employee();
    employee.fromJSON(res.entity);
    return employee;
  });

export const getEmployeeDetails = (employeeId) =>
  employeeDetailsApi(employeeId)
    .then((res) => getEmployeeObjectFromRes(res.entity))
    .catch((err) => null);

export const getLeaderboard = ({ trophyId }) =>
  getLeaderboardApi({ trophyId })
    .then((res) => parseLeaderboardRes(res.entity))
    .catch((err) => null);

export const getLeaderboardTrophies = ({ teamId }) =>
  getLeaderboardTrophiesApi({ teamId })
    .then((res) =>
      res.entity.map((jsonObject) => {
        const trophy = new HashTag();
        trophy.fromJSON(jsonObject);
        return trophy;
      })
    )
    .catch((err) => null);

export const nominateForTrophy = ({ employeeId, trophyId }) =>
  nominateForTrophyApi({ employeeId, trophyId })
    .then((res) => parseLeaderboardRes(res.entity))
    .catch((err) => null);

export const getNodes = ({ employeeId, limit = 8, offset = 0 }) =>
  getNodesApi({ employeeId, limit, offset }).then((res) => parseNodesRes(res.entity));

export const getRelationships = ({ employeeId, limit = 8, offset = 0 }) =>
  getRelationshipsApi({ employeeId, limit, offset }).then((res) => parseRelationshipsRes(res.entity));

export const login = ({ email, password }) =>
  loginApi({ email, password }).then((res) => {
    const employee = new Employee(null, {
      ...res.entity.employee,
      authToken: res.entity.authToken,
    });
    return { employee, authToken: res.entity.authToken, authenticated: true };
  });

// backend picksup cookie from the session storage
export const authenticate = async ({ landingPageType, landingPageUuid, teamType = null }) => {
  try {
    // Authenticate API call
    const res = await authenticateApi({ landingPageType, landingPageUuid, teamType });
    const { entity } = res;

    // Construct employee object
    const employee = new Employee(null, {
      ...entity.employee,
    });

    // Construct orgData
    const {
      defaultGoalDueTS,
      defaultGoalStartTS,
      okrConfigData,
      includeOwnerGoals,
      includeCollaboratorGoals,
      goalCustomNamesConfig,
      accessPrivileges,
    } = entity;

    return {
      employee,
      authToken: entity.authToken,
      authenticated: true,
      enabledFeatureFlags: res.entity.enabledFeatureFlags || [],
      defaultGoalDueTS,
      defaultGoalStartTS,
      okrConfigData,
      includeOwnerGoals,
      includeCollaboratorGoals,
      goalCustomNamesConfig,
      accessPrivileges,
    };
  } catch (error) {
    // Handle other errors
    console.error('Authentication error:', error);
    throw error; // Or return a default structure if needed
  }
};

export const authenticateNetchex = async ({ jwtToken }) => {
  try {
    // Authenticate API call
    const res = await authenticateNetchexApi({ jwtToken });
    const { entity } = res;

    // Construct employee object
    const employee = new Employee(null, {
      ...entity.employee,
    });

    // Construct orgData
    const {
      defaultGoalDueTS,
      defaultGoalStartTS,
      okrConfigData,
      includeOwnerGoals,
      includeCollaboratorGoals,
      goalCustomNamesConfig,
      accessPrivileges,
    } = entity;

    return {
      employee,
      authToken: entity.authToken,
      authenticated: true,
      enabledFeatureFlags: res.entity.enabledFeatureFlags || [],
      defaultGoalDueTS,
      defaultGoalStartTS,
      okrConfigData,
      includeOwnerGoals,
      includeCollaboratorGoals,
      goalCustomNamesConfig,
      accessPrivileges,
    };
  } catch (error) {
    // Handle other errors
    console.error('Authentication error:', error);
    throw error; // Or return a default structure if needed
  }
};

export const authenticateDemoUser = ({ demoUserEmail }) =>
  authenticateDemoUserApi({ demoUserEmail }).then((res) => {
    const employee = new Employee(null, {
      ...res.entity.employee,
      authToken: res.entity.authToken,
    });

    return {
      employee,
      authToken: res.entity.authToken,
      authenticated: true,
      enabledFeatureFlags: res.entity.enabledFeatureFlags || [],
      orgData: {
        defaultGoalDueTS: res.entity?.defaultGoalDueTS,
        defaultGoalStartTS: res.entity?.defaultGoalStartTS,
        defaultGoalVisibility: res.entity?.employee?.organization?.objectiveSettings?.defaultGoalVisibility ?? 'GLOBAL',
        objectiveCreationAccess: res.entity?.employee?.organization?.objectiveSettings?.objectiveCreationAccess,
      },
    };
  });

export function updateLoggedInEmployee(employee) {
  return function (dispatch) {
    dispatch(updateLoggedInEmployeeAction(employee));
  };
}

export function getAndUpdateEmployeeHelper(employeeId) {
  return function (dispatch) {
    return authenticateApi({})
      .then((res) => {
        const employee = new Employee(null, {
          ...res.entity.employee,
          authToken: res.entity.authToken,
        });
        return employee;
      })
      .then((employee) => dispatch(updateLoggedInEmployee(employee)))
      .catch(() => {});
  };
}

export function getMeshTrophies(employeeId) {
  return getMeshTrophiesApi(employeeId).then((res) =>
    res.entity.map((trohyObject) => {
      const tag = new HashTag();
      tag.fromJSON(trohyObject.orgTag);
      tag.stats = { score: trohyObject.score };
      return tag;
    })
  );
}

export const employeeReliabilityScore = ({ employeeId }) =>
  employeeReliabilityScoreApi({ employeeId }).then((res) => res.entity);

export const getEmployeeTeams = ({ employeeId }) =>
  getEmployeeTeamsApi({ employeeId }).then((res) => ({
    employeeTeams: res?.entity?.employeeTeams
      ? res.entity.employeeTeams.map((teamJSON) => Team.fromJSON(teamJSON))
      : [],
    orgTeams: res?.entity?.orgTeams ? res.entity.orgTeams.map((teamJSON) => OrgTeam.fromJSON(teamJSON)) : [],
    teamInvitations: res?.entity?.teamInvitations
      ? res.entity.teamInvitations.map((inviteJSON) => PendingInvite.fromJSON(inviteJSON))
      : [],
  }));

export const removeEmployee = ({ employeeId }) => removeEmployeeApi({ employeeId }).then((res) => res);

export const setUserTimeZone = async ({ employeeId, timezone }) => {
  try {
    const res = await setUserTimeZoneApi({ employeeId, timezone });
  } catch (error) {
    console.error("Error while setting user's timezone : ", error);
  }
};
